import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { getLocations } from '../../../services/LocationService';
import { getLogo } from '../../../services/LogoStorageService';
import CampaignService from '../../../services/CampaignService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { getIcon } from '../../../utils/IconHandler';

const emptyReward = () => ({
  name: '',
  rewardText: '',
  discountType: 'percentage',
  discountPercentage: 10,
  discountAmount: 0,
  maxDiscountAmount: '',
  weight: 1,
  maxDailyEmissions: '',
  maxTotalEmissions: '',
  enabled: true
});

const emptyCampaign = () => ({
  id: null,
  name: 'Promo PostVenta',
  enabled: false,
  printTicket: true,
  sendEmail: false,
  salesChannel: 'both',
  locationIds: [],
  startDate: '',
  endDate: '',
  minimumSpend: 0,
  requireRegisteredClient: false,
  excludeStaff: true,
  emissionPercentage: 100,
  maxDailyEmissions: '',
  maxTotalEmissions: '',
  onePerClientPerDay: false,
  validityDays: 30,
  ticketTitle: 'Promo PostVenta',
  ticketDescription: '¡Tenés una promo para tu próxima compra!',
  ticketSubdescription: 'Tenés una promo para tu próxima compra.',
  ticketTerms: 'Código válido por única vez.',
  ticketFooter: '¡Gracias por tu compra!',
  rewards: [emptyReward()]
});

const numberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCampaign = (campaign) => ({
  ...emptyCampaign(),
  ...campaign,
  printTicket: campaign?.printTicket ?? emptyCampaign().printTicket,
  sendEmail: campaign?.sendEmail ?? emptyCampaign().sendEmail,
  salesChannel: campaign?.salesChannel || emptyCampaign().salesChannel,
  ticketSubdescription: campaign?.ticketSubdescription ?? campaign?.ticketDescription ?? emptyCampaign().ticketSubdescription,
  startDate: campaign?.startDate || '',
  endDate: campaign?.endDate || '',
  maxDailyEmissions: campaign?.maxDailyEmissions ?? '',
  maxTotalEmissions: campaign?.maxTotalEmissions ?? '',
  rewards: (campaign?.rewards?.length ? campaign.rewards : [emptyReward()]).map((reward) => ({
    ...emptyReward(),
    ...reward,
	    maxDailyEmissions: reward.maxDailyEmissions ?? '',
	    maxTotalEmissions: reward.maxTotalEmissions ?? '',
	    maxDiscountAmount: reward.maxDiscountAmount ?? ''
	  }))
});

const firstPreviewReward = (rewards) => rewards?.find((reward) => reward.enabled !== false) || rewards?.[0] || emptyReward();

const TicketPreview = ({ campaign, logoUrl, companyName }) => {
  const reward = firstPreviewReward(campaign.rewards);
  const validUntilDay = String(Math.min(31, Math.max(1, Number(campaign.validityDays) || 21))).padStart(2, '0');

  return (
    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
      <Box
        sx={{
          width: 250,
          maxWidth: '100%',
          bgcolor: '#fff',
          color: '#111',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          px: 1.5,
          py: 2,
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center'
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={companyName}
            sx={{ display: 'block', maxWidth: 110, maxHeight: 52, objectFit: 'contain', mx: 'auto', mb: 2 }}
          />
        ) : (
          <Typography sx={{ fontFamily: 'serif', fontSize: 28, lineHeight: 1, mb: 2 }}>{companyName}</Typography>
        )}
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{campaign.ticketTitle || 'Promo PostVenta'}</Typography>
        <Typography sx={{ fontSize: 11 }}>Ticket N°: 28944</Typography>
        <Typography sx={{ fontSize: 11, mb: 1 }}>Fecha: 22/07/2026 18:14</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace', mb: 1 }}>{'-'.repeat(34)}</Typography>
        {campaign.ticketDescription && (
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.75 }}>{campaign.ticketDescription}</Typography>
        )}
        {campaign.ticketSubdescription && (
          <Typography sx={{ fontSize: 13, mb: 2 }}>{campaign.ticketSubdescription}</Typography>
        )}
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>{reward.rewardText || 'Texto para el cliente'}</Typography>
        <Typography sx={{ fontSize: 11 }}>Código de descuento</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>P6W4RYGS</Typography>
        <Typography sx={{ fontSize: 11, mb: 1.5 }}>Válido hasta: 2026-08-{validUntilDay}</Typography>
        {campaign.ticketTerms && (
          <>
            <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>{'-'.repeat(34)}</Typography>
            <Typography sx={{ fontSize: 11, mb: 1 }}>{campaign.ticketTerms}</Typography>
          </>
        )}
        {campaign.ticketFooter && (
          <>
            <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>{'-'.repeat(34)}</Typography>
            <Typography sx={{ fontSize: 12 }}>{campaign.ticketFooter}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const PostSaleCampaignTab = () => {
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState(emptyCampaign());
  const [previewLogoUrl, setPreviewLogoUrl] = useState(null);
  const [previewCompanyName, setPreviewCompanyName] = useState('Empresa');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selectedLocationsText = useMemo(() => {
    if (!formData.locationIds.length) return 'Todas las sucursales';
    return locations
      .filter((location) => formData.locationIds.includes(Number(location.id)))
      .map((location) => location.name)
      .join(', ');
  }, [formData.locationIds, locations]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsResponse, locationsResponse] = await Promise.all([
        CampaignService.getPostSaleCampaigns(),
        getLocations()
      ]);
      const list = Array.isArray(campaignsResponse) ? campaignsResponse : [];
      setLocations(locationsResponse || []);
      if (list.length > 0) setFormData(normalizeCampaign(list[0]));
    } catch (err) {
      setError('Error al cargar campañas PostVenta');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadPreviewBranding = async () => {
      try {
        const logo = await getLogo();
        if (!mounted) return;
        setPreviewLogoUrl(logo || null);
        setPreviewCompanyName('Empresa');
      } catch (err) {
        if (!mounted) return;
        setPreviewLogoUrl(null);
        setPreviewCompanyName('Empresa');
      }
    };

    loadPreviewBranding();
    return () => {
      mounted = false;
    };
  }, []);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateReward = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rewards: prev.rewards.map((reward, rewardIndex) => (
        rewardIndex === index ? { ...reward, [field]: value } : reward
      ))
    }));
  };

  const toggleLocation = (locationId) => {
    const id = Number(locationId);
    setFormData((prev) => ({
      ...prev,
      locationIds: prev.locationIds.includes(id)
        ? prev.locationIds.filter((current) => current !== id)
        : [...prev.locationIds, id]
    }));
  };

  const validate = () => {
    if (!formData.name.trim()) return 'El nombre de la campaña es obligatorio.';
    if (Number(formData.emissionPercentage) < 0 || Number(formData.emissionPercentage) > 100) return 'El porcentaje de salida debe estar entre 0 y 100.';
    if (Number(formData.validityDays) < 1) return 'La validez del cupón debe ser de al menos 1 día.';
    if (formData.enabled && !formData.rewards.some((reward) => reward.enabled && Number(reward.weight) > 0)) return 'Agregá al menos una recompensa activa con frecuencia mayor a 0.';
    for (const reward of formData.rewards) {
      if (!reward.rewardText.trim()) return 'Cada recompensa necesita el texto que verá el cliente.';
      if (reward.discountType === 'percentage' && (Number(reward.discountPercentage) <= 0 || Number(reward.discountPercentage) > 100)) return 'Los descuentos por porcentaje deben estar entre 1 y 100.';
      if (reward.discountType === 'amount' && Number(reward.discountAmount) <= 0) return 'Los descuentos por monto deben ser mayores a 0.';
      if (reward.discountType !== 'free_item' && reward.maxDiscountAmount !== '' && Number(reward.maxDiscountAmount) <= 0) return 'El tope de descuento debe ser mayor a 0 o quedar vacío.';
      if (reward.discountType === 'free_item' && Number(reward.discountAmount) <= 0) return 'El artículo gratis necesita un monto de descuento mayor a 0.';
    }
    return null;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...formData,
        salesChannel: formData.salesChannel || 'both',
        minimumSpend: Number(formData.minimumSpend) || 0,
        emissionPercentage: Number(formData.emissionPercentage) || 0,
        validityDays: Number(formData.validityDays) || 1,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        maxDailyEmissions: numberOrNull(formData.maxDailyEmissions),
        maxTotalEmissions: numberOrNull(formData.maxTotalEmissions),
        rewards: formData.rewards.map((reward) => ({
          ...reward,
          name: reward.rewardText,
          enabled: true,
          discountPercentage: Number(reward.discountPercentage) || 0,
          discountAmount: Number(reward.discountAmount) || 0,
          maxDiscountAmount: reward.discountType === 'free_item' ? null : numberOrNull(reward.maxDiscountAmount),
          weight: Number(reward.weight) || 0,
          maxDailyEmissions: numberOrNull(reward.maxDailyEmissions),
          maxTotalEmissions: numberOrNull(reward.maxTotalEmissions),
          freeItemDescription: null
        }))
      };
      const saved = await CampaignService.savePostSaleCampaign(payload, formData.id);
      SnackbarUtils.success('Campaña PostVenta guardada');
      await loadData();
      setFormData(normalizeCampaign(saved));
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message || 'Error al guardar PostVenta';
      setError(message);
      SnackbarUtils.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Paper sx={{ p: 3, mt: 2 }}>Cargando...</Paper>;

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Configuración Post venta</Typography>
          <Typography variant="body2" color="text.secondary">
            Evalúa ventas cerradas (excepto PedidosYa, que tiene su propia pestaña) y, si corresponde, imprime un cupón único de un solo uso.
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={formData.enabled} onChange={(e) => updateField('enabled', e.target.checked)} />}
          label="Habilitar Post venta"
        />
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {formData.enabled && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Dónde aplica</Typography>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ display: 'none' }}>Dónde aplica</FormLabel>
                <RadioGroup
                  row
                  value={formData.salesChannel || 'both'}
                  onChange={(e) => updateField('salesChannel', e.target.value)}
                >
                  <FormControlLabel value="both" control={<Radio />} label="Ambos" />
                  <FormControlLabel value="local" control={<Radio />} label="Solo local" />
                  <FormControlLabel value="web" control={<Radio />} label="Solo web" />
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define si PostVenta evalúa ventas del local, compras web o ambos canales.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Ticket impreso</Typography>
              <FormControlLabel
                control={<Switch checked={formData.printTicket === true} onChange={(e) => updateField('printTicket', e.target.checked)} />}
                label="Imprimir cupón Post Venta al finalizar la venta"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Si está activo, las ventas elegibles imprimen un cupón adicional con código único.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Envío de correo</Typography>
              <FormControlLabel
                control={<Switch checked={formData.sendEmail === true} onChange={(e) => updateField('sendEmail', e.target.checked)} />}
                label="Enviar cupón Post Venta por correo al finalizar la venta"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Si está activo, las ventas elegibles envían el cupón al email del cliente cuando la venta tenga cliente con correo.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Nombre" value={formData.name} onChange={(e) => updateField('name', e.target.value)} helperText="Nombre interno para identificar la campaña." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Monto mínimo" value={formData.minimumSpend} onChange={(e) => updateField('minimumSpend', e.target.value)} helperText="La venta debe alcanzar este total para participar." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="% de salida" value={formData.emissionPercentage} onChange={(e) => updateField('emissionPercentage', e.target.value)} helperText="Chance de imprimir promo en ventas elegibles." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Días de validez" value={formData.validityDays} onChange={(e) => updateField('validityDays', e.target.value)} helperText="Cuántos días será válido el código generado." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={formData.startDate} onChange={(e) => updateField('startDate', e.target.value)} helperText="Opcional. Desde cuándo se evalúa." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="date" label="Fin" InputLabelProps={{ shrink: true }} value={formData.endDate} onChange={(e) => updateField('endDate', e.target.value)} helperText="Opcional. Hasta cuándo se evalúa." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Cupo diario" value={formData.maxDailyEmissions} onChange={(e) => updateField('maxDailyEmissions', e.target.value)} helperText="Máximo de cupones emitidos por día. Vacío significa sin límite." />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Cupo total" value={formData.maxTotalEmissions} onChange={(e) => updateField('maxTotalEmissions', e.target.value)} helperText="Máximo histórico de cupones emitidos. Vacío significa sin límite." />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Restricciones</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Checkbox checked={formData.requireRegisteredClient} onChange={(e) => updateField('requireRegisteredClient', e.target.checked)} />} label="Solo clientes registrados" />
              <Typography variant="caption" color="text.secondary" display="block">Si está activo, las ventas sin cliente no participan.</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Checkbox checked={formData.excludeStaff} onChange={(e) => updateField('excludeStaff', e.target.checked)} />} label="Excluir staff" />
              <Typography variant="caption" color="text.secondary" display="block">Evita emitir promos si el cliente asociado coincide con alguien del equipo.</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Checkbox checked={formData.onePerClientPerDay} onChange={(e) => updateField('onePerClientPerDay', e.target.checked)} />} label="Una por cliente por día" />
              <Typography variant="caption" color="text.secondary" display="block">Limita a un cupón diario por cliente para esta campaña.</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Sucursales</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Si no seleccionás ninguna, aplica en todas. Actual: {selectedLocationsText}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {locations.map((location) => (
                  <FormControlLabel
                    key={location.id}
                    control={<Checkbox checked={formData.locationIds.includes(Number(location.id))} onChange={() => toggleLocation(location.id)} />}
                    label={location.name}
                  />
                ))}
              </Box>
            </Grid>

            {formData.printTicket === true && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2">Contenido del ticket</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Textos que se imprimen en el cupón entregado al cliente.</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Título del ticket" value={formData.ticketTitle} onChange={(e) => updateField('ticketTitle', e.target.value)} helperText="Encabezado visible en el cupón impreso." />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Descripción" value={formData.ticketDescription} onChange={(e) => updateField('ticketDescription', e.target.value)} helperText="Línea principal destacada debajo del encabezado." />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Subdescripción" value={formData.ticketSubdescription} onChange={(e) => updateField('ticketSubdescription', e.target.value)} helperText="Línea secundaria debajo de la descripción." />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Condiciones" value={formData.ticketTerms} onChange={(e) => updateField('ticketTerms', e.target.value)} helperText="Texto de restricciones que se imprime debajo del código." />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Texto final" value={formData.ticketFooter} onChange={(e) => updateField('ticketFooter', e.target.value)} helperText="Cierre opcional del cupón." />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" align="center" sx={{ mb: 2 }}>Vista previa</Typography>
                    <TicketPreview campaign={formData} logoUrl={previewLogoUrl} companyName={previewCompanyName} />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1">Recompensas posibles</Typography>
                  <Typography variant="body2" color="text.secondary">Si la campaña sale seleccionada, se elige una recompensa según su frecuencia relativa.</Typography>
                </Box>
                <Button variant="outlined" onClick={() => updateField('rewards', [...formData.rewards, emptyReward()])}>Agregar recompensa</Button>
              </Box>
            </Grid>

            {formData.rewards.map((reward, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <TextField fullWidth label="Texto para el cliente" value={reward.rewardText} onChange={(e) => updateReward(index, 'rewardText', e.target.value)} helperText="Es lo que se imprime como beneficio." />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="number" label="Frecuencia" value={reward.weight} onChange={(e) => updateReward(index, 'weight', e.target.value)} helperText="Mayor número, más chances relativas." />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        color="error"
                        variant="outlined"
                        disabled={formData.rewards.length === 1}
                        onClick={() => updateField('rewards', formData.rewards.filter((_, rewardIndex) => rewardIndex !== index))}
                        sx={{ height: 56, minHeight: 56, py: 0 }}
                      >
                        Quitar
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select label="Tipo" value={reward.discountType} onChange={(e) => updateReward(index, 'discountType', e.target.value)}>
                          <MenuItem value="percentage">Porcentaje</MenuItem>
                          <MenuItem value="amount">Monto fijo</MenuItem>
                          <MenuItem value="free_item">Artículo gratis</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {reward.discountType === 'percentage' && (
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth type="number" label="% descuento" value={reward.discountPercentage} onChange={(e) => updateReward(index, 'discountPercentage', e.target.value)} helperText="Porcentaje que aplicará el código." />
                      </Grid>
                    )}
                    {reward.discountType === 'amount' && (
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth type="number" label="Monto descuento" value={reward.discountAmount} onChange={(e) => updateReward(index, 'discountAmount', e.target.value)} helperText="Monto que aplicará el código." />
                      </Grid>
                    )}
                    {reward.discountType === 'free_item' ? (
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth type="number" label="Monto descuento" value={reward.discountAmount} onChange={(e) => updateReward(index, 'discountAmount', e.target.value)} helperText="Monto que aplicará el código." />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth type="number" label="Tope descuento" value={reward.maxDiscountAmount} onChange={(e) => updateReward(index, 'maxDiscountAmount', e.target.value)} helperText="Opcional. Máximo a descontar." />
                      </Grid>
                    )}
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="number" label="Cupo diario" value={reward.maxDailyEmissions} onChange={(e) => updateReward(index, 'maxDailyEmissions', e.target.value)} helperText="Opcional. Máximo por día." />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="number" label="Cupo total" value={reward.maxTotalEmissions} onChange={(e) => updateReward(index, 'maxTotalEmissions', e.target.value)} helperText="Opcional. Máximo histórico." />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={getIcon('save')}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PostSaleCampaignTab;
